import React, { useState, useEffect } from 'react';
import { obtenerTodosControlPagos, pagoEfectivo, procesarTransferencia, obtenerCentro, verificarPagosAnterioresHistorial, obtenerFechasPago } from '../../services/pagosServices';
import { obtenerPlanes } from '../../services/centroService';
import { obtenerMatriculaPorId, actualizarMatricula } from '../../services/matriculaService';
import { 
    Loader, 
    Check, 
    Search, 
    Calendar,
    Filter, 
    SlidersHorizontal, 
    ExternalLink, 
    AlertTriangle,
    DollarSign,
    Clock,
    Calendar as CalendarIcon,
    ChevronDown,
    Users,
    X,
    CreditCard
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const Button = ({ children, onClick, className = '', variant = 'primary', size = 'default' }) => {
    const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';
    
    const variantStyles = {
        primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
        ghost: 'bg-transparent text-gray-600 hover:bg-gray-50',
        green: 'bg-emerald-500 text-white hover:bg-emerald-600',
        danger: 'bg-rose-500 text-white hover:bg-rose-600',
        blue: 'bg-blue-500 text-white hover:bg-blue-600'
    };

    const sizeStyles = {
        default: 'px-4 py-2 text-sm',
        sm: 'px-3 py-1.5 text-xs',
        lg: 'px-6 py-3 text-base',
        icon: 'p-2',
        xs: 'p-1 text-xs'
    };

    return (
        <button
            onClick={onClick}
            className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        >
            {children}
        </button>
    );
};

const PagoEfectivo = () => {
    const navigate = useNavigate();
    const [pagos, setPagos] = useState([]);
    const [pagosFiltrados, setPagosFiltrados] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [procesandoPago, setProcesandoPago] = useState(false);
    const [filtroActivo, setFiltroActivo] = useState('periodo');
    const [filtros, setFiltros] = useState({
        busqueda: '',
        periodo: 'todos',
        fechaPersonalizada: {
            mes: new Date().getMonth(),
            year: new Date().getFullYear()
        }
    });
    const [pagosPorPadre, setPagosPorPadre] = useState({});
    const [precioCentro, setPrecioCentro] = useState(0);
    const [preciosPorMatricula, setPreciosPorMatricula] = useState({});
    const [confirmacionPago, setConfirmacionPago] = useState(null);
    const [confirmacionPagoGrupal, setConfirmacionPagoGrupal] = useState(null);
    const [confirmacionCancelarTransferencia, setConfirmacionCancelarTransferencia] = useState(null);
    
    // Estados para selección de planes
    const [mostrarSeleccionPlan, setMostrarSeleccionPlan] = useState(false);
    const [hijoEnSeleccion, setHijoEnSeleccion] = useState(null);
    const [matriculaEnSeleccion, setMatriculaEnSeleccion] = useState(null);
    const [planesDisponibles, setPlanesDisponibles] = useState([]);
    const [planSeleccionado, setPlanSeleccionado] = useState(null);
    const [planActual, setPlanActual] = useState(null);
    const [planesActualesPorMatricula, setPlanesActualesPorMatricula] = useState({});
    const [planesSeleccionadosTemporales, setPlanesSeleccionadosTemporales] = useState({});

    const cargarPreciosPlanes = async (pagos) => {
        try {
            const precios = {};
            const planesActuales = {};
            
            // Obtener todos los planes disponibles
            const planesResponse = await obtenerPlanes();
            const todosLosPlanes = planesResponse?.data || [];
            
            // Obtener precios y planes actuales de cada matrícula
            for (const pago of pagos) {
                if (!precios[pago.id_matricula]) {
                    // Usar el monto que viene directamente del API
                    const monto = parseFloat(pago.monto) || 0;
                    precios[pago.id_matricula] = monto;
                    
                    // Obtener el plan actual de la matrícula
                    try {
                        const matriculaResponse = await obtenerMatriculaPorId(pago.nino.id_child);
                        if (matriculaResponse?.success && matriculaResponse.matricula?.matricula?.id_plan) {
                            const idPlanActual = matriculaResponse.matricula.matricula.id_plan;
                            const planActual = todosLosPlanes.find(plan => plan.id_plan === idPlanActual);
                            if (planActual) {
                                planesActuales[pago.id_matricula] = planActual.nombre;
                                console.log(`Plan actual para matrícula ${pago.id_matricula}: ${planActual.nombre}`);
                            } else {
                                console.log(`No se encontró plan con ID ${idPlanActual} para matrícula ${pago.id_matricula}`);
                            }
                        } else {
                            console.log(`No se pudo obtener id_plan para matrícula ${pago.id_matricula}`);
                        }
                    } catch (error) {
                        console.error(`Error al obtener plan actual para matrícula ${pago.id_matricula}:`, error);
                    }
                }
            }
            
            setPreciosPorMatricula(precios);
            setPlanesActualesPorMatricula(planesActuales);
            
            // Establecer precio por defecto (primer precio encontrado o 0)
            const preciosArray = Object.values(precios).filter(p => p > 0);
            setPrecioCentro(preciosArray.length > 0 ? preciosArray[0] : 0);
            
        } catch (err) {
            console.error('Error al cargar precios de planes:', err);
            showErrorToast('Error al obtener precios de los planes');
        }
    };

    const cargarPagos = async () => {
        try {
            const response = await obtenerTodosControlPagos();
            if (response && response.data) {
                const pagosPendientes = response.data.filter(
                    pago => (pago.estado === 'Pendiente' || 
                    (pago.tipo_pago === 'Transferencia' && pago.estado_verificacion === 'Pendiente')) &&
                    pago.padre && pago.padre.id_usuario // Asegurar que el padre existe
                );
                setPagos(pagosPendientes);
                
                // Cargar precios de planes primero
                await cargarPreciosPlanes(pagosPendientes);
                
                const grupos = pagosPendientes.reduce((acc, pago) => {
                    const idPadre = pago.padre.id_usuario;
                    if (!acc[idPadre]) {
                        acc[idPadre] = {
                            padre: pago.padre,
                            pagos: [],
                            montoTotal: 0,
                            seleccionados: []
                        };
                    }
                    
                    // Obtener precio específico para esta matrícula
                    const precioMatricula = preciosPorMatricula[pago.id_matricula] || 0;
                    acc[idPadre].pagos.push({...pago, monto: precioMatricula});
                    acc[idPadre].montoTotal += precioMatricula;
                    return acc;
                }, {});
                
                setPagosPorPadre(grupos);
                setPagosFiltrados(Object.values(grupos));
            }
        } catch (err) {
            showErrorToast('Error al obtener datos de la base de datos');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        cargarPagos();
    }, []);

    useEffect(() => {
        if (filtros.busqueda === '') {
            setPagosPorPadre(pagosFiltrados);
            return;
        }

        const searchTermLower = filtros.busqueda.toLowerCase();
        const gruposFiltrados = pagosFiltrados.reduce((acc, grupo) => {
            const padreCumple = grupo.padre.nombre.toLowerCase().includes(searchTermLower);
            const ninosCumplen = grupo.pagos.some(pago => 
                pago.nino.nombre.toLowerCase().includes(searchTermLower)
            );

            if (padreCumple) {
                acc[grupo.padre.id_usuario] = {...grupo};
            } else if (ninosCumplen) {
                acc[grupo.padre.id_usuario] = {
                    ...grupo,
                    pagos: grupo.pagos.filter(pago => 
                        pago.nino.nombre.toLowerCase().includes(searchTermLower)
                    )
                };
                acc[grupo.padre.id_usuario].montoTotal = acc[grupo.padre.id_usuario].pagos.reduce((sum, pago) => sum + (preciosPorMatricula[pago.id_matricula] || 0), 0);
            }
            return acc;
        }, {});

        setPagosPorPadre(gruposFiltrados);
    }, [filtros.busqueda, pagosFiltrados]);

    const filtrarPorPeriodo = (pagos) => {
        const fechaActual = new Date();
        
        switch (filtros.periodo) {
            case 'actual':
                return pagos.filter(pago => {
                    const fechaPago = new Date(pago.proxima_fecha_pago);
                    return fechaPago.getMonth() === fechaActual.getMonth() &&
                           fechaPago.getFullYear() === fechaActual.getFullYear();
                });
            case 'proximo':
                return pagos.filter(pago => {
                    const fechaPago = new Date(pago.proxima_fecha_pago);
                    const siguienteMes = new Date(fechaActual.setMonth(fechaActual.getMonth() + 1));
                    return fechaPago.getMonth() === siguienteMes.getMonth() &&
                           fechaPago.getFullYear() === siguienteMes.getFullYear();
                });
            case 'personalizado':
                return pagos.filter(pago => {
                    const fechaPago = new Date(pago.proxima_fecha_pago);
                    return fechaPago.getMonth() === filtros.fechaPersonalizada.mes &&
                           fechaPago.getFullYear() === filtros.fechaPersonalizada.year;
                });
            case 'todos':
                return pagos;
            default:
                return pagos;
        }
    };

    useEffect(() => {
        if (filtros.busqueda === '') {
            const pagosFiltradosPorPeriodo = pagosFiltrados.reduce((acc, grupo) => {
                const pagosFiltrados = filtrarPorPeriodo(grupo.pagos);
                if (pagosFiltrados.length > 0) {
                    acc[grupo.padre.id_usuario] = {
                        ...grupo,
                        pagos: pagosFiltrados,
                        montoTotal: pagosFiltrados.reduce((sum, pago) => sum + (preciosPorMatricula[pago.id_matricula] || 0), 0)
                    };
                }
                return acc;
            }, {});
            setPagosPorPadre(pagosFiltradosPorPeriodo);
            return;
        }
        // ... rest of existing search filter code ...
    }, [filtros.busqueda, pagosFiltrados, filtros.periodo, filtros.fechaPersonalizada.mes, filtros.fechaPersonalizada.year]);

    const seleccionarPago = (idPadre, idControl) => {
        const nuevosPagosPorPadre = { ...pagosPorPadre };
        const grupo = nuevosPagosPorPadre[idPadre];
        
        if (grupo.seleccionados.includes(idControl)) {
            grupo.seleccionados = grupo.seleccionados.filter(id => id !== idControl);
        } else {
            grupo.seleccionados.push(idControl);
        }
        
        setPagosPorPadre(nuevosPagosPorPadre);
    };

    const seleccionarTodosPagos = (idPadre) => {
        const nuevosPagosPorPadre = { ...pagosPorPadre };
        const grupo = nuevosPagosPorPadre[idPadre];
        
        if (grupo.seleccionados.length === grupo.pagos.length) {
            grupo.seleccionados = [];
        } else {
            grupo.seleccionados = grupo.pagos.map(pago => pago.id_control);
        }
        
        setPagosPorPadre(nuevosPagosPorPadre);
    };

    const procesarPagosSeleccionados = async (idPadre) => {
        const grupo = pagosPorPadre[idPadre];
        const pagosAProcesar = grupo.pagos.filter(pago => 
            grupo.seleccionados.includes(pago.id_control)
        );
        
        if (pagosAProcesar.length === 0) {
            showWarningToast('Seleccione al menos un pago para procesar');
            return;
        }

        const montoTotal = Number(pagosAProcesar.reduce((sum, pago) => sum + (preciosPorMatricula[pago.id_matricula] || 0), 0).toFixed(2));
        
        setConfirmacionPagoGrupal({
            pagos: pagosAProcesar,
            montoTotal,
            padre: grupo.padre
        });
    };

    const procesarPago = async (idControl, monto, idMatricula, proximaFechaPago) => {
        setProcesandoPago(true);
        try {
            const fechaPago = new Date(proximaFechaPago);
            const cobrador = JSON.parse(localStorage.getItem('user'));

            const precioMatricula = preciosPorMatricula[idMatricula] || 0;
            
            // Verificar si hay un plan seleccionado temporalmente para esta matrícula
            const planTemporal = planesSeleccionadosTemporales[idMatricula];
            if (planTemporal) {
                // Buscar el pago para obtener el id_child
                const pagoEncontrado = pagos.find(p => p.id_matricula === parseInt(idMatricula));
                if (pagoEncontrado) {
                    // Actualizar el plan en la matrícula antes de procesar el pago
                    try {
                        await actualizarMatricula(pagoEncontrado.nino.id_child, {
                            id_plan: planTemporal.id_plan
                        });
                        console.log('Plan actualizado antes del pago:', planTemporal.nombre);
                        
                        // Limpiar el plan temporal después de actualizar
                        const nuevosTemporales = { ...planesSeleccionadosTemporales };
                        delete nuevosTemporales[idMatricula];
                        setPlanesSeleccionadosTemporales(nuevosTemporales);
                        
                        showSuccessToast(`Plan actualizado a "${planTemporal.nombre}" y pago procesado`);
                    } catch (updateError) {
                        console.error('Error al actualizar plan antes del pago:', updateError);
                        showErrorToast('Error al actualizar el plan, pero el pago continuará');
                    }
                }
            }

            const response = await pagoEfectivo(
                precioMatricula,
                idMatricula,
                fechaPago.getMonth() + 1,
                fechaPago.getFullYear(),
                cobrador.nombre
            );

            if (response.success) {
                await cargarPagos();
                return true;
            } else {
                throw new Error('Error al procesar el pago');
            }
        } catch (err) {
            showErrorToast(err.message || 'Error al procesar el pago');
            return false;
        } finally {
            setProcesandoPago(false);
            setConfirmacionPago(null);
            setConfirmacionPagoGrupal(null);
        }
    };

    // Función para verificar pagos anteriores
    const verificarPagosAnteriores = async (idMatricula) => {
        try {
            const response = await verificarPagosAnterioresHistorial(idMatricula);
            return response && response.data && response.data.length > 0;
        } catch (error) {
            console.error('Error al verificar pagos anteriores:', error);
            return false;
        }
    };

    // Función para obtener planes disponibles
    const obtenerPlanesDisponibles = async (idMatricula) => {
        try {
            // Obtener planes reales del sistema
            const planesResponse = await obtenerPlanes();
            
            if (!planesResponse || !planesResponse.success || !planesResponse.data) {

                throw new Error('No se pudieron obtener los planes');
            }
            

            
            // Filtrar solo planes activos (el campo es 'estado', no 'activo')
            const planesActivos = planesResponse.data.filter(plan => plan.estado === true);

            
            // Convertir los planes a formato esperado y asegurar que monto sea numérico
            const planesFormateados = planesActivos.map(plan => ({
                id_plan: plan.id_plan,
                nombre: plan.nombre,
                monto: parseFloat(plan.monto) || 0,
                descripcion: plan.descripcion || `Plan ${plan.nombre}`,
                tipo: plan.tipo
            }));
            

            return planesFormateados;
        } catch (error) {
            console.error('Error al obtener planes:', error);
            showErrorToast('Error al obtener planes disponibles');
            return [];
        }
    };

    // Función para manejar la selección de plan
    const handleSeleccionarPlan = async (pago) => {
        try {
            // Obtener planes disponibles del sistema
            const planes = await obtenerPlanesDisponibles(pago.id_matricula);
            
            if (planes.length === 0) {
                showErrorToast('No hay planes disponibles');
                return;
            }
            
            // Obtener el plan actual desde la matrícula usando el id_matricula directamente
            const matriculaResponse = await obtenerMatriculaPorId(pago.nino.id_child);
            let planActualEncontrado = null;
            let planActualId = null;
            
            console.log('Respuesta de matrícula:', matriculaResponse);
            console.log('Planes disponibles:', planes);
            
            if (matriculaResponse && matriculaResponse.success) {
                // La respuesta viene en matriculaResponse.matricula.matricula según el controlador
                const matriculaData = matriculaResponse.matricula;
                console.log('Datos de matrícula completos:', JSON.stringify(matriculaData, null, 2));
                
                if (matriculaData && matriculaData.matricula) {
                    const matricula = matriculaData.matricula;
                    console.log('Propiedades de la matrícula:', Object.keys(matricula));
                    console.log('id_plan en matrícula:', matricula.id_plan);
                    
                    // Obtener el id_plan de la matrícula
                    planActualId = matricula.id_plan;
                    
                    if (planActualId) {
                        console.log('ID del plan actual encontrado:', planActualId);
                        
                        // Buscar el plan actual en los planes disponibles
                        planActualEncontrado = planes.find(plan => {
                            console.log('Comparando plan:', plan.id_plan, 'con plan actual:', planActualId);
                            return parseInt(plan.id_plan) === parseInt(planActualId);
                        });
                        
                        console.log('Plan actual encontrado:', planActualEncontrado);
                    } else {
                        console.log('No se encontró id_plan en la matrícula');
                    }
                } else {
                    console.log('Estructura de matrícula inesperada:', matriculaData);
                }
            } else {
                console.log('Respuesta de matrícula no válida o sin éxito:', matriculaResponse);
            }
            
            // Establecer el plan actual y el plan seleccionado
            setHijoEnSeleccion(pago.nino);
            setMatriculaEnSeleccion(pago.id_matricula);
            setPlanesDisponibles(planes);
            setPlanActual(planActualEncontrado); // Guardar el plan actual para mostrarlo
            
            // Verificar si hay un plan seleccionado temporalmente para esta matrícula
            const planTemporal = planesSeleccionadosTemporales[pago.id_matricula];
            
            if (planTemporal) {
                // Si hay un plan temporal, buscar ese plan en los disponibles
                const planTemporalEncontrado = planes.find(plan => 
                    parseInt(plan.id_plan) === parseInt(planTemporal.id_plan)
                );
                if (planTemporalEncontrado) {
                    setPlanSeleccionado(planTemporalEncontrado);
                    console.log('Plan preseleccionado (temporal):', planTemporalEncontrado);
                } else {
                    // Si el plan temporal no está disponible, usar el actual
                    setPlanSeleccionado(planActualEncontrado || planes[0]);
                    console.log('Plan temporal no encontrado, usando actual o primero');
                }
            } else if (planActualEncontrado) {
                // Si no hay plan temporal, usar el plan actual
                setPlanSeleccionado(planActualEncontrado);
                console.log('Plan preseleccionado (actual):', planActualEncontrado);
            } else {
                // Si no hay plan actual, usar el primer plan disponible
                setPlanSeleccionado(planes[0]);
                console.log('Plan preseleccionado (primero):', planes[0]);
            }
            
            setMostrarSeleccionPlan(true);
        } catch (error) {
            console.error('Error al seleccionar plan:', error);
            showErrorToast('Error al cargar opciones de plan');
        }
    };

    // Función para confirmar selección de plan
    const confirmarSeleccionPlan = async () => {
        if (planSeleccionado && matriculaEnSeleccion) {
            const idMatricula = matriculaEnSeleccion.toString();
            console.log('Actualizando matrícula:', idMatricula, 'con plan:', planSeleccionado.nombre);
            
            // Actualizar precio localmente
            const nuevosPrecios = { ...preciosPorMatricula };
            nuevosPrecios[idMatricula] = planSeleccionado.monto;
            setPreciosPorMatricula(nuevosPrecios);
            
            // Actualizar también los planes actuales por matrícula localmente
            const nuevosPlanes = { ...planesActualesPorMatricula };
            nuevosPlanes[idMatricula] = planSeleccionado.nombre;
            setPlanesActualesPorMatricula(nuevosPlanes);
            
            // Guardar el plan seleccionado temporalmente para usar en el pago
            const planesSeleccionadosTemp = { ...planesSeleccionadosTemporales };
            planesSeleccionadosTemp[idMatricula] = {
                id_plan: planSeleccionado.id_plan,
                nombre: planSeleccionado.nombre,
                monto: planSeleccionado.monto
            };
            setPlanesSeleccionadosTemporales(planesSeleccionadosTemp);
            
            console.log('Estados actualizados:');
            console.log('Nuevos precios:', nuevosPrecios);
            console.log('Nuevos planes:', nuevosPlanes);
            console.log('Planes temporales:', planesSeleccionadosTemp);
        }
        
        setMostrarSeleccionPlan(false);
        setPlanSeleccionado(null);
        setHijoEnSeleccion(null);
        setMatriculaEnSeleccion(null);
        setPlanesDisponibles([]);
        setPlanActual(null);
    };

    const procesarPagosGrupales = async (pagos) => {
        setProcesandoPago(true);
        let exitosos = 0;
        
        for (const pago of pagos) {
            const resultado = await procesarPago(
                pago.id_control,
                preciosPorMatricula[pago.id_matricula] || 0,
                pago.id_matricula,
                pago.proxima_fecha_pago
            );
            if (resultado) exitosos++;
        }

        if (exitosos > 0) {
            showSuccessToast(`Se procesaron ${exitosos} pagos exitosamente`);
        }
    };

    const cancelarTransferenciaYPagarEfectivo = async (pago) => {
        setProcesandoPago(true);
        try {
            // Primero rechazar la transferencia
            await procesarTransferencia(pago.id_control, 'rechazar');
            
            // Luego procesar el pago en efectivo
            const fechaPago = new Date(pago.proxima_fecha_pago);
            const cobrador = JSON.parse(localStorage.getItem('user'));
            const precioMatricula = preciosPorMatricula[pago.id_matricula] || 0;
            
            const response = await pagoEfectivo(
                precioMatricula,
                pago.id_matricula,
                fechaPago.getMonth() + 1,
                fechaPago.getFullYear(),
                cobrador.nombre
            );

            if (response.success) {
                showSuccessToast('Transferencia cancelada y pago procesado en efectivo exitosamente');
                await cargarPagos();
                return true;
            } else {
                throw new Error('Error al procesar el pago en efectivo');
            }
        } catch (err) {
            showErrorToast(err.message || 'Error al cancelar transferencia y procesar pago');
            return false;
        } finally {
            setProcesandoPago(false);
            setConfirmacionCancelarTransferencia(null);
        }
    };

    const obtenerPeriodoPago = (fecha) => {
        const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const fechaPago = new Date(fecha);
        const mes = meses[fechaPago.getMonth()];
        const anio = fechaPago.getFullYear();
        
        return `${mes} ${anio}`;
    };

    const renderFiltroActivo = () => {
        switch(filtroActivo) {
            case 'nombre':
                return (
                    <div className="relative">
                        <input
                            type="text"
                            value={filtros.busqueda}
                            onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                            placeholder="Buscar por nombre de padre o niño..."
                            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                        />
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    </div>
                );
            case 'periodo':
                return (
                    <div className="space-y-4">
                        <select
                            value={filtros.periodo}
                            onChange={(e) => setFiltros(prev => ({ ...prev, periodo: e.target.value }))}
                            className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                        >
                            <option value="todos">Todos los periodos</option>
                            <option value="actual">Periodo actual</option>
                            <option value="proximo">Próximo periodo</option>
                            <option value="personalizado">Periodo personalizado</option>
                        </select>
                        
                        {filtros.periodo === 'personalizado' && (
                            <div className="flex space-x-2">
                                <select
                                    className="w-1/2 rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                                    value={filtros.fechaPersonalizada.mes}
                                    onChange={(e) => setFiltros(prev => ({
                                        ...prev,
                                        fechaPersonalizada: {
                                            ...prev.fechaPersonalizada,
                                            mes: parseInt(e.target.value)
                                        }
                                    }))}
                                >
                                    {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((mes, index) => (
                                        <option key={index} value={index}>{mes}</option>
                                    ))}
                                </select>
                                <select
                                    className="w-1/2 rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                                    value={filtros.fechaPersonalizada.year}
                                    onChange={(e) => setFiltros(prev => ({
                                        ...prev,
                                        fechaPersonalizada: {
                                            ...prev.fechaPersonalizada,
                                            year: parseInt(e.target.value)
                                        }
                                    }))}
                                >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i - 2).map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    const limpiarFiltros = () => {
        setFiltros({
            busqueda: '',
            periodo: 'todos',
            fechaPersonalizada: {
                mes: new Date().getMonth(),
                year: new Date().getFullYear()
            }
        });
        setFiltroActivo('periodo');
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Loader className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ToastContainer />
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                <div className="p-4 sm:p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-white">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center">
                                <DollarSign className="w-6 h-6 mr-2 text-indigo-600" />
                                Gestión de Cobros
                            </h1>
                            <p className="text-sm text-slate-500 mt-1 ml-8">Administra y procesa los pagos pendientes de matrícula mensual</p>
                        </div>
                    </div>

                    <div className="mt-4 sm:mt-6">
                        {/* Filtros en móvil */}
                        <div className="block sm:hidden">
                            <select
                                onChange={(e) => setFiltroActivo(e.target.value)}
                                value={filtroActivo}
                                className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 text-sm mb-4"
                            >
                                <option value="nombre">Buscar</option>
                                <option value="periodo">Periodo</option>
                            </select>
                        </div>

                        {/* Filtros en desktop */}
                        <div className="hidden sm:flex gap-2">
                            <Button
                                onClick={() => setFiltroActivo('nombre')}
                                variant="ghost"
                                className={`px-4 py-2 ${filtroActivo === 'nombre' ? 'bg-indigo-50 text-indigo-600' : ''}`}
                            >
                                Buscar
                            </Button>
                            <Button
                                onClick={() => setFiltroActivo('periodo')}
                                variant="ghost"
                                className={`px-4 py-2 ${filtroActivo === 'periodo' ? 'bg-indigo-50 text-indigo-600' : ''}`}
                            >
                                Periodo
                            </Button>
                        </div>

                        {filtroActivo && (
                            <div className="mt-4">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                    <div className="flex-1 w-full">
                                        {renderFiltroActivo()}
                                    </div>
                                    {(filtros.busqueda || filtros.periodo !== 'actual') && (
                                        <button
                                            onClick={limpiarFiltros}
                                            className="text-sm text-slate-500 hover:text-slate-700 transition-colors duration-200 whitespace-nowrap flex items-center gap-1"
                                        >
                                            <X className="w-4 h-4" />
                                            Limpiar filtros
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Lista de Pagos */}
                <div className="divide-y divide-slate-200">
                    {Object.keys(pagosPorPadre).length === 0 ? (
                        <div className="p-4 sm:p-8 text-center">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-4">
                                <Search className="w-6 h-6 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 mb-1">No se encontraron pagos</h3>
                            <p className="text-sm text-slate-500">
                                {filtros.busqueda 
                                    ? "No hay pagos que coincidan con tu búsqueda" 
                                    : filtros.periodo !== 'actual'
                                    ? "No hay pagos pendientes para el periodo seleccionado"
                                    : "No hay pagos pendientes por mostrar"}
                            </p>
                        </div>
                    ) : (
                        Object.entries(pagosPorPadre).map(([idPadre, grupo]) => (
                            <div key={idPadre} className="p-4 hover:bg-slate-50 transition-colors">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                                    <div className="flex items-center space-x-4">
                                        <div className="bg-indigo-100 rounded-full p-2">
                                            <Users className="h-5 w-5 text-indigo-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-base sm:text-lg font-semibold text-slate-900">{grupo.padre.nombre}</h2>
                                            <p className="text-xs sm:text-sm text-slate-500">
                                                {grupo.pagos.length} cobros pendientes - Total: ${grupo.montoTotal.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => seleccionarTodosPagos(idPadre)}
                                            className="text-xs sm:text-sm flex items-center gap-1"
                                        >
                                            {grupo.seleccionados.length === grupo.pagos.length ? (
                                                <>
                                                    <X className="w-3 h-3" />
                                                    Deseleccionar Todo
                                                </>
                                            ) : (
                                                <>
                                                    <Check className="w-3 h-3" />
                                                    Seleccionar Todo
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant={grupo.seleccionados.length > 0 ? "primary" : "ghost"}
                                            size="sm"
                                            onClick={() => procesarPagosSeleccionados(idPadre)}
                                            className={`text-xs sm:text-sm flex items-center gap-1 ${grupo.seleccionados.length === 0 ? 'opacity-50' : ''}`}
                                            disabled={grupo.seleccionados.length === 0}
                                        >
                                            <DollarSign className="w-3 h-3" />
                                            Cobrar {grupo.seleccionados.length > 0 ? `(${grupo.seleccionados.length})` : 'Seleccionados'}
                                        </Button>
                                    </div>
                                </div>

                                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                                    {grupo.pagos.map(pago => {
                                        const tieneTransferenciaPendiente = pago.tipo_pago === 'Transferencia' && pago.estado_verificacion === 'Pendiente';
                                        return (
                                        <div 
                                            key={pago.id_control}
                                            className={`relative border rounded-lg p-3 sm:p-4 transition-all shadow-sm hover:shadow-md ${
                                                tieneTransferenciaPendiente
                                                    ? 'border-orange-300 bg-orange-50 ring-1 ring-orange-200'
                                                    : grupo.seleccionados.includes(pago.id_control) 
                                                    ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300'
                                                    : 'border-slate-200 hover:border-indigo-200'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1">
                                                    <h3 className="font-medium text-sm sm:text-base text-slate-900">{pago.nino.nombre}</h3>
                                                    <div className="flex items-center mt-1 text-xs sm:text-sm text-slate-500">
                                                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                                        <span className="whitespace-nowrap">{obtenerPeriodoPago(pago.proxima_fecha_pago)}</span>
                                                    </div>
                                                    {tieneTransferenciaPendiente && (
                                                        <div className="flex items-center mt-2 text-xs text-orange-600">
                                                            <CreditCard className="w-3 h-3 mr-1" />
                                                            <span>Transferencia pendiente</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="relative">
                                                    <div className={`absolute -top-1 -right-1 transition-all ${grupo.seleccionados.includes(pago.id_control) ? 'opacity-100' : 'opacity-0'}`}>
                                                        <div className="bg-indigo-500 text-white rounded-full p-0.5">
                                                            <Check className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        checked={grupo.seleccionados.includes(pago.id_control)}
                                                        onChange={() => seleccionarPago(idPadre, pago.id_control)}
                                                        className="h-5 w-5 sm:h-6 sm:w-6 rounded-md border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                        aria-label={`Seleccionar pago de ${pago.nino.nombre}`}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100">
                                                <div>
                                                    <span className="font-semibold text-base sm:text-lg text-slate-900">${(preciosPorMatricula[pago.id_matricula] || 0).toFixed(2)}</span>
                                                    <p className="text-xs text-slate-500">Pago mensual</p>
                                                    {planesActualesPorMatricula[pago.id_matricula] && (
                                                        <p className="text-xs text-indigo-600 font-medium mt-1">
                                                            Plan: {planesActualesPorMatricula[pago.id_matricula]}
                                                        </p>
                                                    )}
                                                </div>
                                                {tieneTransferenciaPendiente ? (
                                                    <div className="flex flex-col gap-1">
                                                        <Button
                                                            variant="danger"
                                                            size="xs"
                                                            onClick={() => setConfirmacionCancelarTransferencia(pago)}
                                                            disabled={procesandoPago}
                                                            className="text-xs flex items-center gap-1"
                                                        >
                                                            <X className="w-3 h-3" />
                                                            Cancelar y cobrar
                                                        </Button>
                                                        <p className="text-xs text-orange-600 text-center">en efectivo</p>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-1">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => setConfirmacionPago(pago)}
                                                            disabled={procesandoPago}
                                                            className="text-xs sm:text-sm flex items-center gap-1 hover:shadow-md"
                                                        >
                                                            <DollarSign className="w-3 h-3" />
                                                            Cobrar individual
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            onClick={() => handleSeleccionarPlan(pago)}
                                                            disabled={procesandoPago}
                                                            className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                                                        >
                                                            <SlidersHorizontal className="w-3 h-3" />
                                                            Cambiar plan
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Modales con diseño responsive */}
            {confirmacionPago && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-auto">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="bg-indigo-100 rounded-full p-2">
                                <DollarSign className="h-5 w-5 text-indigo-600" />
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900">Confirmar Cobro</h3>
                        </div>
                        <p className="text-sm sm:text-base text-slate-600 mb-6">
                            ¿Confirmar cobro de <span className="font-semibold">${(preciosPorMatricula[confirmacionPago.id_matricula] || 0).toFixed(2)}</span> para {confirmacionPago.nino.nombre}?
                        </p>
                        <div className="flex justify-end gap-3">
                            <Button 
                                variant="ghost" 
                                onClick={() => setConfirmacionPago(null)}
                                disabled={procesandoPago}
                                size="sm"
                                className="text-xs sm:text-sm"
                            >
                                Cancelar
                            </Button>
                            <Button 
                                variant="primary"
                                onClick={() => procesarPagosGrupales([confirmacionPago])}
                                disabled={procesandoPago}
                                size="sm"
                                className="text-xs sm:text-sm"
                            >
                                {procesandoPago ? (
                                    <><Loader className="w-4 h-4 animate-spin mr-2" /> Procesando...</>
                                ) : (
                                    'Confirmar'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {confirmacionPagoGrupal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-auto shadow-xl">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="bg-indigo-100 rounded-full p-2">
                                <Users className="h-5 w-5 text-indigo-600" />
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900">Confirmar Cobros Grupales</h3>
                        </div>
                        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mb-6">
                            <div className="flex justify-between items-center mb-2">
                                <p className="text-sm text-slate-700">Padre/Madre:</p>
                                <p className="text-sm font-medium text-slate-900">{confirmacionPagoGrupal.padre.nombre}</p>
                            </div>
                            <div className="flex justify-between items-center mb-2">
                                <p className="text-sm text-slate-700">Número de pagos:</p>
                                <p className="text-sm font-medium text-slate-900">{confirmacionPagoGrupal.pagos.length}</p>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-indigo-200">
                                <p className="text-sm font-semibold text-slate-700">Total a cobrar:</p>
                                <p className="text-sm font-bold text-indigo-700">${confirmacionPagoGrupal.montoTotal}</p>
                            </div>
                        </div>
                        <p className="text-sm sm:text-base text-slate-600 mb-6 text-center">
                            ¿Confirmar el procesamiento de todos estos pagos seleccionados?
                        </p>
                        <div className="flex justify-end gap-3">
                            <Button 
                                variant="ghost" 
                                onClick={() => setConfirmacionPagoGrupal(null)}
                                disabled={procesandoPago}
                                size="sm"
                                className="text-xs sm:text-sm"
                            >
                                Cancelar
                            </Button>
                            <Button 
                                variant="primary"
                                onClick={() => procesarPagosGrupales(confirmacionPagoGrupal.pagos)}
                                disabled={procesandoPago}
                                size="sm"
                                className="text-xs sm:text-sm"
                            >
                                {procesandoPago ? (
                                    <><Loader className="w-4 h-4 animate-spin mr-2" /> Procesando...</>
                                ) : (
                                    'Confirmar'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación para cancelar transferencia */}
            {confirmacionCancelarTransferencia && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mx-auto shadow-xl">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="bg-orange-100 rounded-full p-2">
                                <AlertTriangle className="h-5 w-5 text-orange-600" />
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900">Cancelar Transferencia</h3>
                        </div>
                        <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 mb-4">
                            <p className="text-sm text-orange-800 mb-2">
                                <strong>Estudiante:</strong> {confirmacionCancelarTransferencia.nino.nombre}
                            </p>
                            <p className="text-sm text-orange-800 mb-2">
                                <strong>Periodo:</strong> {obtenerPeriodoPago(confirmacionCancelarTransferencia.proxima_fecha_pago)}
                            </p>
                            <p className="text-sm text-orange-800">
                                <strong>Monto:</strong> ${(preciosPorMatricula[confirmacionCancelarTransferencia.id_matricula] || 0).toFixed(2)}
                            </p>
                        </div>
                        <p className="text-sm sm:text-base text-slate-600 mb-6 text-center">
                            ¿Está seguro de que desea cancelar la transferencia pendiente y procesar el pago en efectivo?
                        </p>
                        <div className="flex justify-end gap-3">
                            <Button 
                                variant="ghost" 
                                onClick={() => setConfirmacionCancelarTransferencia(null)}
                                disabled={procesandoPago}
                                size="sm"
                                className="text-xs sm:text-sm"
                            >
                                Cancelar
                            </Button>
                            <Button 
                                variant="danger"
                                onClick={() => cancelarTransferenciaYPagarEfectivo(confirmacionCancelarTransferencia)}
                                disabled={procesandoPago}
                                size="sm"
                                className="text-xs sm:text-sm"
                            >
                                {procesandoPago ? (
                                    <><Loader className="w-4 h-4 animate-spin mr-2" /> Procesando...</>
                                ) : (
                                    'Confirmar'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Selección de Plan */}
            {mostrarSeleccionPlan && hijoEnSeleccion && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full mx-auto max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="bg-indigo-100 rounded-full p-2">
                                    <SlidersHorizontal className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-900">
                                        Seleccionar Plan para {hijoEnSeleccion.nombre}
                                    </h3>
                                    <p className="text-sm text-slate-500">Elige el plan de pago para este estudiante</p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                    setMostrarSeleccionPlan(false);
                                    setPlanSeleccionado(null);
                                    setHijoEnSeleccion(null);
                                    setPlanesDisponibles([]);
                                    setPlanActual(null);
                                }}
                                className="text-slate-400 hover:text-slate-600"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {/* Plan Actual */}
                            {planActual && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <h4 className="font-medium text-blue-900">Plan Actual</h4>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-blue-800">{planActual.nombre}</p>
                                            <p className="text-sm text-blue-600">${planActual.monto.toFixed(2)} - {planActual.hora_inicio} a {planActual.hora_fin}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                Actual
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Planes Disponibles */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-slate-900">Seleccionar plan:</h4>
                                {planesDisponibles.length === 0 ? (
                                    <div className="text-center py-8">
                                        <div className="text-slate-400 mb-2">
                                            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <p className="text-slate-500 text-sm">No hay planes disponibles</p>
                                        <p className="text-slate-400 text-xs mt-1">Verifique la consola para más detalles</p>
                                    </div>
                                ) : (
                                    planesDisponibles.map((plan) => (
                                        <div
                                            key={plan.id_plan}
                                            className={`border rounded-lg p-4 cursor-pointer transition-all ${
                                                planSeleccionado?.id_plan === plan.id_plan
                                                    ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300'
                                                    : 'border-slate-200 hover:border-indigo-200 hover:bg-slate-50'
                                            }`}
                                            onClick={() => setPlanSeleccionado(plan)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3">
                                                <input
                                                    type="radio"
                                                    name="plan"
                                                    checked={planSeleccionado?.id_plan === plan.id_plan}
                                                    onChange={() => setPlanSeleccionado(plan)}
                                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300"
                                                />
                                                <div>
                                                    <div className="flex items-center space-x-2">
                                                        <h5 className="font-medium text-slate-900">{plan.nombre}</h5>
                                                        {planActual?.id_plan === plan.id_plan && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                Actual
                                                            </span>
                                                        )}
                                                        {planSeleccionado?.id_plan === plan.id_plan && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                                Seleccionado
                                                            </span>
                                                        )}
                                                    </div>
                                                            <div className="text-sm text-slate-500">
                                                                {plan.descripcion.includes(';') ? (
                                                                    // Si contiene punto y coma, dividir y mostrar la primera parte normal y el resto como viñetas
                                                                    (() => {
                                                                        const partes = plan.descripcion.split(';');
                                                                        const primeraParte = partes[0].trim();
                                                                        const restoParts = partes.slice(1).filter(item => item.trim());
                                                                        
                                                                        return (
                                                                            <div>
                                                                                {primeraParte && <div className="mb-2">{primeraParte}</div>}
                                                                                {restoParts.map((item, index) => {
                                                                                    const trimmedItem = item.trim();
                                                                                    return (
                                                                                        <div key={index} className="flex items-start space-x-2 mb-1">
                                                                                            <span className="text-indigo-500 mt-1">•</span>
                                                                                            <span>{trimmedItem}</span>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        );
                                                                    })()
                                                                ) : (
                                                                    // Si no contiene punto y coma, mostrar como texto normal
                                                                    <span>{plan.descripcion}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-lg font-semibold text-slate-900">
                                                        ${plan.monto.toFixed(2)}
                                                    </span>
                                                    <p className="text-xs text-slate-500">por mes</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t border-slate-200">
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setMostrarSeleccionPlan(false);
                                    setPlanSeleccionado(null);
                                    setHijoEnSeleccion(null);
                                    setPlanesDisponibles([]);
                                    setPlanActual(null);
                                }}
                                className="flex-1 text-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={confirmarSeleccionPlan}
                                disabled={!planSeleccionado}
                                className={`flex-1 text-sm ${
                                    !planSeleccionado ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                {planActual && planSeleccionado && planSeleccionado.id_plan !== planActual.id_plan 
                                    ? 'Cambiar Plan' 
                                    : 'Confirmar Plan'
                                }
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PagoEfectivo;